import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';
import RequirementsCard from './RequirementsCard';
import EditRequirementsCard from './EditRequirementsCard';
import JobPostCard from './JobPostCard';
import OverallScoreBanner from './OverallScoreBanner';
import AdviceContentBox from '../../components/AdviceContentBox';
import NiceTabsExample from './NiceTabsExample';
import { bg } from '../../utils/darkModeAdapter';

const API_BASE_URL = process.env.REACT_APP_API_URL;

/* ========== メイン画面 ========== */
const MainContent = ({
  requirements,
  jobPostMd,
  setJobPostMd,
  onRequirementsUpdate,
  jobPostAdvice,
  isEditingRequirements,
  setIsEditingRequirements,
  chatMode,
  drawerOpen,
  aiAnalysis,
  setAiAnalysis
}) => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isMdDown = useMediaQuery(theme.breakpoints.down('md'));
  const [tabValue, setTabValue] = useState(1);
  const [editedRequirements, setEditedRequirements] = useState({...requirements});

  // 分析結果の状態管理
  const [analysis, setAnalysis] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [shouldHighlight, setShouldHighlight] = useState(false);

  // AI品質チェックの状態管理（aiAnalysis/setAiAnalysisは親からpropとして受け取る）
  const [isAiAnalyzing, setIsAiAnalyzing] = useState(false);
  const [aiError, setAiError] = useState(null);

  // 品質チェックカードへのref
  const analysisCardRef = useRef(null);

  // 品質チェック実行関数（手動トリガー用）
  const runQualityCheck = useCallback(async () => {
    if (!jobPostMd || jobPostMd.trim() === '') {
      return;
    }

    setIsAnalyzing(true);
    setShouldHighlight(true);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/job_posting/analyze`,
        {
          content: jobPostMd,
          salary: requirements?.salary || ''
        },
        { withCredentials: true }
      );

      if (response.data.status === 'success') {
        setAnalysis(response.data.analysis);
        // チェック完了後、結果にスクロール
        setTimeout(() => {
          analysisCardRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }, 100);
      }
    } catch (error) {
      console.error('分析エラー:', error);
      setAnalysis(null);
    } finally {
      setIsAnalyzing(false);
      // ハイライトを3秒後に解除
      setTimeout(() => setShouldHighlight(false), 3000);
    }
  }, [jobPostMd, requirements?.salary]);

  // AI品質チェック実行関数
  const runAiQualityCheck = useCallback(async () => {
    if (!jobPostMd || jobPostMd.trim() === '') {
      return;
    }

    setIsAiAnalyzing(true);
    setAiError(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/job_posting/ai_analyze`,
        {
          content: jobPostMd,
          title: requirements?.role || '',
          salary: requirements?.salary || ''
        },
        { withCredentials: true, timeout: 120000 }
      );

      if (response.data.status === 'success') {
        setAiAnalysis(response.data);
      }
    } catch (error) {
      console.error('AI品質チェックエラー:', error);
      const isTimeout = error.code === 'ECONNABORTED' || error.code === 'ERR_NETWORK';
      const msg = isTimeout
        ? 'AI品質チェックがタイムアウトしました。もう一度お試しください。'
        : (error.response?.data?.detail || 'AI品質チェックでエラーが発生しました');
      setAiError(msg);
      setAiAnalysis(null);
    } finally {
      setIsAiAnalyzing(false);
    }
  }, [jobPostMd, requirements?.role, requirements?.salary]);

  // ジョブポスト更新ハンドラー
  const handleJobPostUpdate = (updatedMd) => {
    // 親コンポーネントに更新を伝える
    console.log("Updating job post content:", updatedMd.substring(0, 50) + "...");
    setJobPostMd(updatedMd);
  };

  // 求人票が変更されたら分析を実行
  useEffect(() => {
    const analyzeJobPost = async () => {
      if (!jobPostMd || jobPostMd.trim() === '') {
        setAnalysis(null);
        return;
      }

      setIsAnalyzing(true);
      try {
        const response = await axios.post(
          `${API_BASE_URL}/job_posting/analyze`,
          {
            content: jobPostMd,
            salary: requirements?.salary || ''
          },
          { withCredentials: true }
        );

        if (response.data.status === 'success') {
          setAnalysis(response.data.analysis);
        }
      } catch (error) {
        console.error('分析エラー:', error);
        // エラー時は分析結果をクリア（UIにはエラー表示しない）
        setAnalysis(null);
      } finally {
        setIsAnalyzing(false);
      }
    };

    // デバウンス: 500ms後に分析を実行
    const timeoutId = setTimeout(analyzeJobPost, 500);
    return () => clearTimeout(timeoutId);
  }, [jobPostMd, requirements?.salary]);

  // サイドチャットのマージンは親コンポーネントで処理するので削除

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      sx={{
        flexGrow: 1,
        overflow: 'auto',
        py: isSmDown ? 2 : 3,
        px: { xs: 1, sm: 2, md: 3 },
        backgroundColor: bg(theme).card,
        maxWidth: '100%',
        mx: 'auto',
        height: '100%',
        // marginRight: rightMargin, - 削除
        transition: 'padding 0.3s ease',
      }}
    >
      <NiceTabsExample
        tabValue={tabValue}
        setTabValue={setTabValue}
      />

      <Box sx={{ 
        height: 'calc(100% - 48px)', // タブの高さを引く
        overflow: 'auto',
        mt: 2,
        width: '100%',
      }}>
        {tabValue === 0 && (
          <Box sx={{ 
            maxWidth: '100%',
            mx: 'auto',
          }}>
            {isEditingRequirements ? (
              <EditRequirementsCard
                requirements={requirements}
                onUpdate={onRequirementsUpdate}
                onCancel={() => setIsEditingRequirements(false)}
              />
            ) : (
              <RequirementsCard
                requirements={requirements}
                onEdit={() => setIsEditingRequirements(true)}
              />
            )}
          </Box>
        )}

        {tabValue === 1 && (
          <Box sx={{
            maxWidth: '100%',
            mx: 'auto',
            flexGrow: 1,
            overflow: 'auto', // スクロールを有効化
            pb: 2, // スクロール時に下部に余白を追加
          }}>
            {/* AI アドバイス表示エリア */}
            {jobPostAdvice && (
              <Box sx={{ mb: isSmDown ? 2 : 3 }}>
                <AdviceContentBox adviceObj={jobPostAdvice} />
              </Box>
            )}

            {/* 総合スコアバナー（AI補足情報を統合） */}
            <Box ref={analysisCardRef}>
              <OverallScoreBanner
                analysis={analysis}
                isLoading={isAnalyzing}
                aiAnalysis={aiAnalysis}
                isAiLoading={isAiAnalyzing}
              />
            </Box>

            <JobPostCard
              jobPostMd={jobPostMd}
              setJobPostMd={handleJobPostUpdate}
              onRequestQualityCheck={runQualityCheck}
              isCheckingQuality={isAnalyzing}
              analysis={analysis}
              onRequestAiQualityCheck={runAiQualityCheck}
              isAiChecking={isAiAnalyzing}
              aiAnalysis={aiAnalysis}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default MainContent; 